<template>
  <div class="step-card">
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            d="M4 20V6C4 4.9 4.9 4 6 4H14C15.1 4 16 4.9 16 6V20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />

          <path
            d="M16 10H19C19.6 10 20 10.4 20 11V20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />

          <path
            d="M8 8H12M8 12H12M8 16H12M3 20H21"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Основная информация
        </span>

        <h3>
          Как называется компания?
        </h3>
      </div>
    </div>

    <p class="description">
      Укажите реальное и узнаваемое название компании.
      Не добавляйте организационно-правовую форму:
      ООО, ИП, АО и другие аббревиатуры.
    </p>

    <div class="form-section">
      <div class="field-heading">
        <label
          class="field-label"
          for="company-name"
        >
          Название компании
        </label>

        <span class="character-counter">
          {{ nameLength }}/80
        </span>
      </div>

      <div
        class="input-wrapper"
        :class="{
          invalid: isTouched && !isValid,
          valid: isTouched && isValid
        }"
      >
        <div class="input-icon">
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M4 20V6C4 4.9 4.9 4 6 4H14C15.1 4 16 4.9 16 6V20"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />

            <path
              d="M8 9H12M8 13H12M8 17H12M3 20H18"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <input
          id="company-name"
          v-model="model.name"
          type="text"
          class="text-input"
          placeholder="Например: Yandex"
          maxlength="80"
          autocomplete="organization"
          spellcheck="false"
          @input="onInput"
          @blur="isTouched = true"
        />

        <transition name="status">
          <div
            v-if="isTouched && isValid"
            class="input-status valid-status"
          >
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M5 12.5L10 17L19 7"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </transition>
      </div>

      <transition name="message" mode="out-in">
        <p
          v-if="isTouched && !isValid"
          key="error"
          class="field-message error"
        >
          <span class="message-icon">!</span>

          Название должно содержать минимум два символа
        </p>

        <p
          v-else
          key="hint"
          class="field-message hint"
        >
          Это название будет отображаться в профиле компании,
          вакансиях и результатах поиска.
        </p>
      </transition>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  watch
} from "vue";

const model = defineModel({
  required: true
});

const emit = defineEmits([
  "valid",
  "notValid"
]);

const isTouched = ref(false);

const normalizedName = computed(() => {
  return model.value.name?.trim() || "";
});

const nameLength = computed(() => {
  return model.value.name?.length || 0;
});

const isValid = computed(() => {
  return normalizedName.value.length >= 2;
});

function onInput() {
  isTouched.value = true;
}

watch(
  () => model.value.name,
  (value) => {
    if (
      value?.trim().length > 0
    ) {
      isTouched.value = true;
    }

    if (isValid.value) {
      emit("valid");
    } else {
      emit("notValid");
    }
  },
  {
    immediate: true
  }
);
</script>

<style scoped>
.step-card {
  width: 100%;

  padding:
    26px
    24px;

  box-sizing: border-box;

  background: #ffffff;
  border-radius: 28px;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  max-width: 620px;

  margin-bottom: 18px;
}

.step-icon {
  width: 46px;
  height: 46px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #287ff0;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.13),
      rgba(83, 104, 232, 0.08)
    );

  border: 1px solid rgba(40, 127, 240, 0.12);
  border-radius: 14px;
}

.step-icon svg {
  width: 25px;
  height: 25px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 3px;
}

.step-label {
  color: #969ba5;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.step-heading h3 {
  margin: 0;

  color: #24272d;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 19px;
  line-height: 1.3;
}

.description {
  max-width: 620px;

  margin:
    0
    0
    23px;

  color: #777c86;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.55;
}

.form-section {
  width: 100%;
  max-width: 540px;
}

.field-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;

  margin-bottom: 8px;
}

.field-label {
  color: #40444c;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
}

.character-counter {
  color: #a1a5ad;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.input-wrapper {
  width: 100%;
  height: 52px;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  background: #f5f6f8;
  border: 1px solid #dfe2e7;
  border-radius: 14px;

  box-shadow:
    inset 0 1px 2px
    rgba(24, 34, 55, 0.035);

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.input-wrapper:focus-within {
  background: #ffffff;
  border-color: #5b8dfd;

  box-shadow:
    0 0 0 3px
    rgba(91, 141, 253, 0.14),
    inset 0 1px 2px
    rgba(24, 34, 55, 0.04);
}

.input-wrapper.invalid {
  border-color: #e36a72;

  box-shadow:
    0 0 0 3px
    rgba(222, 75, 87, 0.09);
}

.input-wrapper.valid {
  border-color: rgba(52, 170, 111, 0.5);
}

.input-icon {
  width: 48px;
  height: 100%;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #8c929d;
}

.input-icon svg {
  width: 20px;
  height: 20px;
}

.input-wrapper:focus-within
.input-icon {
  color: #4b83ec;
}

.text-input {
  min-width: 0;
  height: 100%;
  flex: 1;

  padding: 0 8px 0 0;

  box-sizing: border-box;

  color: #202329;
  background: transparent;

  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
}

.text-input::placeholder {
  color: #a6aab2;
}

.input-status {
  width: 29px;
  height: 29px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-right: 11px;

  border-radius: 9px;
}

.input-status svg {
  width: 16px;
  height: 16px;
}

.valid-status {
  color: #24965c;
  background: rgba(42, 174, 105, 0.1);
}

.field-message {
  min-height: 20px;

  display: flex;
  align-items: flex-start;
  gap: 7px;

  margin:
    8px
    2px
    0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.45;
}

.hint {
  color: #9297a1;
}

.error {
  color: #ce4d58;
}

.message-icon {
  width: 17px;
  height: 17px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-top: -1px;

  color: #ffffff;
  background: #db5964;
  border-radius: 50%;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

/* Анимации */

.status-enter-active,
.status-leave-active,
.message-enter-active,
.message-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.message-enter-from,
.message-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

/* Тёмная тема */

:global(html[data-theme="dark"]) .step-card {
  background: #1c1c1e;
}

:global(html[data-theme="dark"])
.step-heading h3 {
  color: #f0f1f3;
}

:global(html[data-theme="dark"])
.step-label {
  color: #81858e;
}

:global(html[data-theme="dark"])
.description {
  color: #a7aab1;
}

:global(html[data-theme="dark"])
.field-label {
  color: #d4d6da;
}

:global(html[data-theme="dark"])
.character-counter {
  color: #7f838b;
}

:global(html[data-theme="dark"])
.input-wrapper {
  background: #292a2e;
  border-color: #414349;

  box-shadow:
    inset 0 1px 2px
    rgba(0, 0, 0, 0.2);
}

:global(html[data-theme="dark"])
.input-wrapper:focus-within {
  background: #2d2f34;
  border-color: #5989eb;

  box-shadow:
    0 0 0 3px
    rgba(70, 126, 229, 0.15);
}

:global(html[data-theme="dark"])
.text-input {
  color: #ffffff;
}

:global(html[data-theme="dark"])
.text-input::placeholder {
  color: #70747c;
}

:global(html[data-theme="dark"])
.input-icon {
  color: #7f848e;
}

:global(html[data-theme="dark"])
.hint {
  color: #7f838b;
}

@media (max-width: 640px) {
  .step-card {
    padding:
      22px
      16px;
  }

  .step-heading h3 {
    font-size: 18px;
  }

  .form-section {
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .input-wrapper,
  .status-enter-active,
  .status-leave-active,
  .message-enter-active,
  .message-leave-active {
    transition: none;
  }
}

/* DARK THEME */

html[data-theme="dark"] .step-card {
  background-color: #1c1c1e;
}

html[data-theme="dark"] .step-icon {
  color: #65a4ff;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.18),
      rgba(103, 88, 232, 0.12)
    );

  border-color: rgba(80, 150, 255, 0.2);
}

html[data-theme="dark"] .step-heading h3 {
  color: #f3f3f5;
}

html[data-theme="dark"] .step-label {
  color: #868a93;
}

html[data-theme="dark"] .description {
  color: #a6a9b0;
}

html[data-theme="dark"] .field-label {
  color: #d9dadd;
}

html[data-theme="dark"] .character-counter {
  color: #858991;
}

html[data-theme="dark"] .input-wrapper {
  background-color: #292a2e;
  border-color: #3d3f45;

  box-shadow:
    inset 0 1px 2px
    rgba(0, 0, 0, 0.22);
}

html[data-theme="dark"] .input-wrapper:focus-within {
  background-color: #2d2f34;
  border-color: #5c91f5;

  box-shadow:
    0 0 0 3px
    rgba(70, 132, 240, 0.17),
    inset 0 1px 2px
    rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"] .input-wrapper.invalid {
  border-color: #e46d76;

  box-shadow:
    0 0 0 3px
    rgba(228, 77, 89, 0.13);
}

html[data-theme="dark"] .input-wrapper.valid {
  border-color: rgba(61, 192, 126, 0.62);
}

html[data-theme="dark"] .input-icon {
  color: #858a94;
}

html[data-theme="dark"] .input-wrapper:focus-within .input-icon {
  color: #67a2ff;
}

html[data-theme="dark"] .text-input {
  color: #f5f5f7;
}

html[data-theme="dark"] .text-input::placeholder {
  color: #737780;
}

html[data-theme="dark"] .valid-status {
  color: #51c989;
  background-color: rgba(61, 192, 126, 0.14);
}

html[data-theme="dark"] .hint {
  color: #858991;
}

html[data-theme="dark"] .error {
  color: #ff8b94;
}

html[data-theme="dark"] .message-icon {
  color: #ffffff;
  background-color: #df5b67;
}
</style>